/**
 * TAGGING SOMEBODY IN A NOTE.
 *
 * Anthony, 2026-09-09: "instead of a chat function yes, use the notes area,
 * but let users tag other users and give them a notification in our
 * notification bell window about it."
 *
 * That is decision 1 from the channels plan, answered, and it is the right
 * answer. A note on a job already IS the conversation about that job -- it
 * has an author, an edit history, photos, and a home on the job, the
 * customer, the lead and the vehicle. The only thing it could not do was
 * reach anybody. You wrote it and hoped somebody looked.
 *
 * So: no second table for conversations, no second place to write the same
 * sentence. An @ in a note, and the bell tells them.
 *
 * ─── WHAT IS STORED, AND WHY IT IS NOT JUST THE TEXT ───────────────────
 *
 * The @ names live in the body, because that is where they were typed and
 * that is what somebody reads back in a year. But WHO was meant is stored
 * separately, as ids, in notes.mentions -- because a name is ambiguous
 * (two Mikes), it changes (people marry), and a note that said @Mike must
 * still be able to say which Mike three years later.
 */

import { db } from "./client.ts";

export type Mate = { id: string; name: string; role: string; handle: string };

/**
 * A HANDLE THAT CAN BE TYPED WITH ONE THUMB.
 *
 * "Robert Palmerston" becomes @robert. First name, lowercased, letters
 * only. Collisions get their surname initial -- @robert and @robertp --
 * rather than a number, because a shop knows which Robert is which and
 * does not know which is 2.
 */
function handles(rows: Array<{ id: string; full_name: string | null; role: string }>): Mate[] {
  const clean = (s: string) => s.toLowerCase().replace(/[^a-z]/g, "");
  const out: Mate[] = rows.map((r) => {
    const name = (r.full_name ?? "").trim() || "Somebody";
    const parts = name.split(/\s+/);
    return {
      id: r.id, name, role: r.role,
      handle: clean(parts[0] ?? "") || clean(name).slice(0, 8) || "someone",
    };
  });
  const seen = new Map<string, Mate[]>();
  for (const m of out) seen.set(m.handle, [...(seen.get(m.handle) ?? []), m]);
  for (const [, group] of seen) {
    if (group.length < 2) continue;
    for (const m of group) {
      const last = m.name.split(/\s+/).slice(1).join("");
      const tail = clean(last).slice(0, 1);
      if (tail) m.handle += tail;
    }
  }
  return out;
}

let cached: Mate[] | null = null;

/**
 * Everybody in this shop who could be tagged. Cached for the session: it
 * is read on every keystroke after an @, and the staff list changes about
 * once a month.
 *
 * Viewers are included. Somebody who can only read is still somebody you
 * might want to point at a job.
 */
export async function mates(): Promise<Mate[]> {
  if (cached) return cached;
  const sb = await db();
  const { data, error } = await sb.from("profiles")
    .select("id,full_name,role,active").eq("active", true).order("full_name");
  if (error) throw new Error(error.message);
  cached = handles((data ?? []) as Array<{ id: string; full_name: string | null; role: string }>);
  return cached;
}

export const forgetMates = (): void => { cached = null; };

/** Who a piece of text is pointing at. Longest handle first, so @robertp
 *  is not matched as @robert with a stray p. */
export function whoIsTagged(body: string, all: Mate[]): Mate[] {
  const found = new Map<string, Mate>();
  const byLength = [...all].sort((a, b) => b.handle.length - a.handle.length);
  for (const m of byLength) {
    /* A handle only counts at a word boundary, so an email address in a
       note does not silently tag half the shop. */
    const re = new RegExp(`(^|[^\\w@])@${m.handle}(?![\\w])`, "i");
    if (re.test(body)) found.set(m.id, m);
  }
  return [...found.values()];
}

/**
 * TELL THEM.
 *
 * One row per person, in the same table the bell already reads. `dedupe`
 * carries the note id and the person, so saving a note twice -- or editing
 * it and tagging the same person again -- does not ring twice.
 *
 * Tagging yourself is silently dropped. Everybody does it once while
 * learning, and a bell that rings for your own note teaches people the
 * bell is noise.
 */
export async function tellThem(opts: {
  noteId: string; who: Mate[]; authorId: string | null; authorName: string;
  accountId: string; body: string; href: string; about: string;
}): Promise<number> {
  const targets = opts.who.filter((m) => m.id !== opts.authorId);
  if (!targets.length) return 0;

  const sb = await db();
  /* The bell shows a title and a line under it. The line is the note
     itself, trimmed -- the point of the alert is to save a trip, so it
     carries as much of the note as fits rather than "you were mentioned". */
  const detail = opts.body.trim().replace(/\s+/g, " ").slice(0, 180);
  const rows = targets.map((m) => ({
    account_id: opts.accountId,
    profile_id: m.id,
    level: "orange",
    kind: "mention",
    dedupe: `mention:${opts.noteId}:${m.id}`,
    title: `${opts.authorName} tagged you on ${opts.about}`,
    detail,
    href: opts.href,
    since_at: new Date().toISOString(),
  }));

  /**
   * ONE INSERT PER PERSON, AND NO CONFLICT TARGET.
   *
   * This was an upsert on (account_id, dedupe) so that saving a note twice
   * could not ring twice. It could never ring ONCE.
   *
   * An insert that names a conflict target makes Postgres check the new
   * row against the table's READ policy as well as its write one. That is
   * the guard which stops somebody using `on conflict` to work out whether
   * a row they are not allowed to see exists. And 0089 narrowed the read on
   * notifications to mine-or-the-whole-shop's:
   *
   *     profile_id is null or profile_id = auth.uid()
   *
   * A mention is addressed to SOMEBODY ELSE by definition. So the row was
   * invisible to the person writing it, and every tag anybody typed came
   * back `new row violates row-level security policy for table
   * "notifications"` with the note already saved beside it. Tagging
   * yourself worked. Tagging a colleague -- the whole feature -- never did,
   * from the day it shipped until today.
   *
   * So the arbiter goes, and the unique index does the same job from the
   * other side: a second save raises 23505 on (account_id, dedupe), which
   * is not a failure. It means their bell is already holding this note.
   *
   * One statement per person rather than one for all of them, because a
   * row that is already rung must not swallow the ones that are not.
   */
  let rung = 0;
  for (const row of rows) {
    const { error } = await sb.from("notifications").insert(row);
    if (error && error.code !== "23505") {
      /**
       * A NOTE THAT SAVED MUST NOT LOOK LIKE A NOTE THAT FAILED.
       *
       * The note is already written by the time this runs. If the bell
       * cannot be rung, the honest thing is to say the tag did not go, not
       * to throw away a note somebody has typed. The caller reports it
       * beside the note.
       */
      throw new Error(error.message);
    }
    rung++;
  }
  return rung;
}
